import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, Banknote, Share, Plus, Download } from "lucide-react";
import { usePWAInstall } from "../hooks/usePWAInstall";

function InstallCard({ isIOS, prompt, onInstall }) {
  return (
    <div className="poster-card border-2 border-gold/40 p-6" data-testid="pwa-install-card">
      <div className="font-heading uppercase tracking-widest text-gold text-lg">The Loaded Street Kitchen App</div>
      <p className="mt-2 text-cream/70 text-sm leading-relaxed">
        Get one-tap access to our menu and your orders — no app store needed.
      </p>

      {isIOS ? (
        <div className="mt-4 flex flex-col gap-2 text-sm text-cream/80">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Share className="h-3.5 w-3.5" />
            </span>
            <span>Tap the <strong className="text-cream">Share</strong> button at the bottom of Safari</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span>Tap <strong className="text-cream">Add to Home Screen</strong></span>
          </div>
        </div>
      ) : prompt ? (
        <button
          onClick={onInstall}
          className="mt-5 w-full btn-poster flex items-center justify-center gap-2"
          data-testid="pwa-install-btn"
        >
          <Download className="h-4 w-4" /> Download
        </button>
      ) : (
        <div className="mt-4 flex flex-col gap-2 text-sm text-cream/80">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0 text-xs font-bold">⋮</span>
            <span>Tap the <strong className="text-cream">menu</strong> button in your browser (top right)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span>Tap <strong className="text-cream">Add to Home Screen</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [installDone, setInstallDone] = useState(false);
  const { prompt, install, isInstalled, isIOS, isAndroid } = usePWAInstall();

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data.order)).catch(() => setErr("Order not found"));
  }, [id]);

  const handleInstall = async () => {
    const accepted = await install();
    if (accepted) setInstallDone(true);
  };

  const isMobile = isIOS || isAndroid;
  const showInstallCard = !isInstalled && !installDone && isMobile;

  if (err) return <div className="min-h-screen bg-ink text-cream flex items-center justify-center">{err}</div>;
  if (!order) return <div className="min-h-screen bg-ink text-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const isCash = order.payment_method === "cash";

  return (
    <div className="min-h-screen bg-ink text-cream flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full space-y-4">

        {/* PWA install card — above confirmation */}
        {showInstallCard && (
          <InstallCard isIOS={isIOS} prompt={prompt} onInstall={handleInstall} />
        )}

        {/* Order confirmed card */}
        <div className="poster-card p-8 text-center" data-testid="order-confirmation-card">
          {isCash ? <Banknote className="h-14 w-14 mx-auto text-gold" /> : <CheckCircle2 className="h-14 w-14 mx-auto text-gold" />}
          <h1 className="mt-6 font-heading uppercase tracking-tight text-cream text-5xl" data-testid="order-confirmation-title">
            {isCash ? "Cash Order Confirmed" : "Order Confirmed"}
          </h1>
          <p className="mt-3 text-cream/75">
            {isCash
              ? `Pay £${order.total_gbp.toFixed(2)} to our driver on delivery. We'll get cooking now.`
              : `We've got your order — hot food is on the way.`}
          </p>

          <div className="mt-6 text-left border border-border p-4">
            <div className="font-heading text-gold uppercase tracking-widest text-sm">Order #{order.id.slice(0, 8)}</div>
            <ul className="mt-3 space-y-1 text-sm">
              {order.items.map((it, i) => (
                <li key={i} className="flex justify-between text-cream/80">
                  <span>{it.quantity}× {it.name}</span>
                  <span>£{it.line_total.toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 border-t border-border pt-2 space-y-1 text-sm">
              <div className="flex justify-between text-cream/70"><span>Subtotal</span><span>£{order.subtotal_gbp.toFixed(2)}</span></div>
              <div className="flex justify-between text-cream/70"><span>Delivery</span><span>£{order.delivery_fee_gbp.toFixed(2)}</span></div>
              <div className="flex justify-between font-heading text-cream"><span>Total</span><span className="text-gold">£{order.total_gbp.toFixed(2)}</span></div>
            </div>
          </div>

          <Link to="/" className="mt-8 inline-block btn-outline-poster" data-testid="back-home-btn">Back to Menu</Link>
        </div>

        {/* PWA install card — below confirmation */}
        {showInstallCard && (
          <InstallCard isIOS={isIOS} prompt={prompt} onInstall={handleInstall} />
        )}

        {installDone && (
          <div className="poster-card border border-green-500/40 p-4 text-center text-green-400 font-heading uppercase tracking-widest text-sm">
            Added to your home screen
          </div>
        )}

      </div>
    </div>
  );
}
